import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, TrendingDown, ChevronLeft, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { generateMockCoinsData } from '../../utils/mockDataGenerator';

const CoinTable: React.FC = () => {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const { coins, totalPages } = generateMockCoinsData(currentPage, 50);

  const formatPrice = (price: number) => {
    if (price >= 1) return `$${price.toFixed(2)}`;
    if (price >= 0.01) return `$${price.toFixed(4)}`;
    return `$${price.toFixed(8)}`;
  };

  const formatNumber = (num: number) => {
    if (num >= 1e12) return `${(num / 1e12).toFixed(2)}T`;
    if (num >= 1e9) return `${(num / 1e9).toFixed(2)}B`;
    if (num >= 1e6) return `${(num / 1e6).toFixed(2)}M`;
    return num.toLocaleString();
  };

  const formatCompactNumber = (num: number) => {
    if (num >= 1e12) return `${(num / 1e12).toFixed(1)}T`;
    if (num >= 1e9) return `${(num / 1e9).toFixed(1)}B`;
    if (num >= 1e6) return `${(num / 1e6).toFixed(1)}M`;
    if (num >= 1e3) return `${(num / 1e3).toFixed(1)}K`;
    return num.toFixed(0);
  };

  const renderSparkline = (prices: number[]) => {
    const min = Math.min(...prices);
    const max = Math.max(...prices);
    const range = max - min;
    const points = prices.slice(-20).map((price, i) => {
      const x = (i / (prices.slice(-20).length - 1)) * 100;
      const y = 100 - ((price - min) / range) * 100;
      return `${x},${y}`;
    }).join(' ');

    const trend = prices[prices.length - 1] > prices[0];

    return (
      <svg width="80" height="32" className="inline-block">
        <polyline
          points={points}
          fill="none"
          stroke={trend ? '#10B981' : '#EF4444'}
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  };

  // Mobile card view for better UX
  const MobileCard = ({ coin }: { coin: any }) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-zinc-900/50 rounded-lg p-4 mb-3 cursor-pointer hover:bg-zinc-900/70 transition-colors"
      onClick={() => navigate(`/coin/${coin.id}`)}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center space-x-3">
          <span className="text-sm text-zinc-500">#{coin.rank}</span>
          <img src={coin.image} alt={coin.name} className="w-8 h-8 rounded-full" />
          <div>
            <div className="font-medium">{coin.name}</div>
            <div className="text-sm text-zinc-500">{coin.symbol.toUpperCase()}</div>
          </div>
        </div>
        <div className="text-right">
          <div className="font-medium">{formatPrice(coin.current_price)}</div>
          <div className={`flex items-center justify-end space-x-1 text-sm ${
            coin.price_change_percentage_24h >= 0 ? 'text-green-500' : 'text-red-500'
          }`}>
            {coin.price_change_percentage_24h >= 0 ? (
              <TrendingUp className="w-3 h-3" />
            ) : (
              <TrendingDown className="w-3 h-3" />
            )}
            <span>{Math.abs(coin.price_change_percentage_24h).toFixed(2)}%</span>
          </div>
        </div>
      </div>
      
      <div className="grid grid-cols-3 gap-2 text-sm">
        <div>
          <div className="text-zinc-500 text-xs">Market Cap</div>
          <div className="font-medium">${formatCompactNumber(coin.market_cap)}</div>
        </div>
        <div>
          <div className="text-zinc-500 text-xs">Volume 24h</div>
          <div className="font-medium">${formatCompactNumber(coin.total_volume)}</div>
        </div>
        <div>
          <div className="text-zinc-500 text-xs">Supply</div>
          <div className="font-medium">{formatCompactNumber(coin.circulating_supply)}</div>
        </div>
      </div>
    </motion.div>
  );

  return (
    <div>
      {/* Desktop Table View */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-zinc-800">
              <th className="text-left py-4 px-4 text-sm font-medium text-zinc-400">#</th>
              <th className="text-left py-4 px-4 text-sm font-medium text-zinc-400">Name</th>
              <th className="text-right py-4 px-4 text-sm font-medium text-zinc-400">Price</th>
              <th className="text-right py-4 px-4 text-sm font-medium text-zinc-400">1h %</th>
              <th className="text-right py-4 px-4 text-sm font-medium text-zinc-400">24h %</th>
              <th className="text-right py-4 px-4 text-sm font-medium text-zinc-400">7d %</th>
              <th className="text-right py-4 px-4 text-sm font-medium text-zinc-400">Market Cap</th>
              <th className="text-right py-4 px-4 text-sm font-medium text-zinc-400 hidden lg:table-cell">Volume (24h)</th>
              <th className="text-right py-4 px-4 text-sm font-medium text-zinc-400 hidden xl:table-cell">Circulating Supply</th>
              <th className="text-center py-4 px-4 text-sm font-medium text-zinc-400">Last 7 Days</th>
            </tr>
          </thead>
          <tbody>
            {coins.map((coin, index) => (
              <motion.tr
                key={coin.id}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3, delay: index * 0.02 }}
                className="border-b border-zinc-800/50 hover:bg-zinc-900/50 cursor-pointer transition-colors"
                onClick={() => navigate(`/coin/${coin.id}`)}
              >
                <td className="py-4 px-4 text-sm text-zinc-400">{coin.rank}</td>
                <td className="py-4 px-4">
                  <div className="flex items-center space-x-3">
                    <img
                      src={coin.image}
                      alt={coin.name}
                      className="w-8 h-8 rounded-full"
                    />
                    <div>
                      <div className="font-medium text-sm">{coin.name}</div>
                      <div className="text-xs text-zinc-500">{coin.symbol.toUpperCase()}</div>
                    </div>
                  </div>
                </td>
                <td className="py-4 px-4 text-right font-mono text-sm">
                  {formatPrice(coin.current_price)}
                </td>
                <td className="py-4 px-4 text-right">
                  <span className={`text-sm ${
                    coin.price_change_percentage_1h >= 0 ? 'text-green-500' : 'text-red-500'
                  }`}>
                    {coin.price_change_percentage_1h >= 0 ? '+' : ''}
                    {coin.price_change_percentage_1h.toFixed(2)}%
                  </span>
                </td>
                <td className="py-4 px-4 text-right">
                  <div className="flex items-center justify-end space-x-1">
                    {coin.price_change_percentage_24h >= 0 ? (
                      <TrendingUp className="w-4 h-4 text-green-500" />
                    ) : (
                      <TrendingDown className="w-4 h-4 text-red-500" />
                    )}
                    <span className={`text-sm font-medium ${
                      coin.price_change_percentage_24h >= 0 ? 'text-green-500' : 'text-red-500'
                    }`}>
                      {Math.abs(coin.price_change_percentage_24h).toFixed(2)}%
                    </span>
                  </div>
                </td>
                <td className="py-4 px-4 text-right">
                  <span className={`text-sm ${
                    coin.price_change_percentage_7d >= 0 ? 'text-green-500' : 'text-red-500'
                  }`}>
                    {coin.price_change_percentage_7d >= 0 ? '+' : ''}
                    {coin.price_change_percentage_7d.toFixed(2)}%
                  </span>
                </td>
                <td className="py-4 px-4 text-right text-sm">
                  <div>
                    <div className="font-medium">${formatNumber(coin.market_cap)}</div>
                    <div className="text-xs text-zinc-500">
                      {((coin.total_volume / coin.market_cap) * 100).toFixed(2)}% Vol
                    </div>
                  </div>
                </td>
                <td className="py-4 px-4 text-right text-sm hidden lg:table-cell">
                  ${formatNumber(coin.total_volume)}
                </td>
                <td className="py-4 px-4 text-right text-sm hidden xl:table-cell">
                  <div>
                    <div>{formatNumber(coin.circulating_supply)}</div>
                    {coin.max_supply && (
                      <div className="text-xs text-zinc-500">
                        Max: {formatNumber(coin.max_supply)}
                      </div>
                    )}
                  </div>
                </td>
                <td className="py-4 px-4 text-center">
                  {renderSparkline(coin.sparkline_in_7d.price)}
                </td>
              </motion.tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile Card View */}
      <div className="md:hidden">
        {coins.map((coin) => (
          <MobileCard key={coin.id} coin={coin} />
        ))}
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between mt-6">
        <button
          onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
          disabled={currentPage === 1}
          className="flex items-center space-x-2 px-4 py-2 bg-zinc-900 rounded-lg hover:bg-zinc-800 
                   disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <ChevronLeft className="w-4 h-4" />
          <span className="text-sm">Previous</span>
        </button>

        <div className="flex items-center space-x-2">
          <span className="text-sm text-zinc-400">
            Page {currentPage} of {totalPages}
          </span>
        </div>

        <button
          onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
          disabled={currentPage === totalPages}
          className="flex items-center space-x-2 px-4 py-2 bg-zinc-900 rounded-lg hover:bg-zinc-800 
                   disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <span className="text-sm">Next</span>
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default CoinTable;